(() => {
  if (customElements.get('lpb-decal-color')) return;
  class LpbDecalColor extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.hex = this.querySelector('[data-color-hex]');
      this.picker = this.querySelector('[data-color-picker]');
      this.sample = this.querySelector('[data-color-sample]');
      this.error = this.querySelector('[data-color-error]');
      this.buttons = [...this.querySelectorAll('[data-swatch]')];
      this.canvas = this.querySelector('[data-decal-preview]');
      this.previewStatus = this.querySelector('[data-preview-status]');
      this.storageKey = 'lpb-decal-color-' + this.dataset.productId;
      this.hex.addEventListener('input', () => this.update(this.hex.value));
      this.hex.addEventListener('change', () => this.validate());
      this.picker.addEventListener('input', () => this.choose(this.picker.value));
      this.buttons.forEach(button => button.addEventListener('click', () => this.choose(button.dataset.swatch)));
      let saved;
      try { saved = sessionStorage.getItem(this.storageKey); } catch (_) {}
      this.choose(saved || this.hex.value);
      if (this.hasAttribute('data-name-decal')) {
        this.nameInput = this.querySelector('[data-custom-name]');
        this.sportInputs = [...this.querySelectorAll('[data-sport]')];
        if (this.sportInputs.length) {
          this.sportArtCache = new Map();
          this.sportInputs.forEach(input => input.addEventListener('change', () => {
            this.querySelector('[data-selected-sport]').textContent = input.value;
            this.loadSportArt();
          }));
          this.querySelectorAll('[data-sport-thumb]').forEach(async canvas => {
            try {
              const art = await this.getSportMask(canvas.dataset.sportArt);
              const ctx = canvas.getContext('2d');
              const scale = Math.min(300 / art.width, 150 / art.height);
              ctx.drawImage(art, (360 - art.width * scale) / 2, (180 - art.height * scale) / 2, art.width * scale, art.height * scale);
            } catch (_) { canvas.setAttribute('hidden', ''); }
          });
          this.loadSportArt();
        }
        this.fontInput = this.querySelector('[data-name-font]');
        this.nameError = this.querySelector('[data-name-error]');
        this.isMemorial = this.hasAttribute('data-memorial-decal');
        if (this.isMemorial) {
          this.birthInput = this.querySelector('[data-memorial-birth]');
          this.deathInput = this.querySelector('[data-memorial-death]');
          this.dateError = this.querySelector('[data-memorial-error]');
          this.memorialThemeRadios = [...this.querySelectorAll('[data-memorial-theme]')];
          if (this.memorialThemeRadios.length) {
            this.memorialArtCache = new Map();
            this.memorialThemeRadios.forEach(radio => radio.addEventListener('change', () => this.loadMemorialTheme()));
            this.loadMemorialTheme();
            this.loadMemorialThumbnails();
          }
          [this.birthInput, this.deathInput].forEach(input => input.addEventListener('input', () => {
            this.checkDates(false);
            this.renderArtwork(this.normalize(this.hex.value) || '#FFFFFF');
          }));
        }
        this.nameInput.addEventListener('input', () => {
          this.checkName(false);
          this.renderArtwork(this.normalize(this.hex.value) || '#FFFFFF');
        });
        this.fontInput.addEventListener('change', () => this.loadNameFonts());
        this.querySelector('[data-font-previous]').addEventListener('click', () => this.stepFont(-1));
        this.querySelector('[data-font-next]').addEventListener('click', () => this.stepFont(1));
        this.parentForm = this.closest('form');
        this.guardSubmission = event => {
          if (event.type === 'click' && !event.target.closest('button[type="submit"], input[type="submit"]')) return;
          if (!this.validate()) {
            event.preventDefault();
            event.stopImmediatePropagation();
          }
        };
        this.parentForm?.addEventListener('click', this.guardSubmission, true);
        this.parentForm?.addEventListener('submit', this.guardSubmission, true);
        this.loadNameFonts();
      } else {
        this.loadArtwork();
      }
      this.dataset.ready = 'true';
    }
    disconnectedCallback() {
      this.thumbnailObserver?.disconnect();
      this.parentForm?.removeEventListener('click', this.guardSubmission, true);
      this.parentForm?.removeEventListener('submit', this.guardSubmission, true);
    }
    normalize(value) {
      let hex = value.trim().replace(/^#/, '');
      if (/^[0-9a-f]{3}$/i.test(hex)) hex = [...hex].map(c => c + c).join('');
      return /^[0-9a-f]{6}$/i.test(hex) ? '#' + hex.toUpperCase() : null;
    }
    choose(value) {
      const hex = this.normalize(value);
      if (!hex) return;
      this.hex.value = hex;
      this.update(hex);
    }
    update(value) {
      const hex = this.normalize(value);
      this.hex.setCustomValidity(hex ? '' : this.dataset.invalidMessage);
      this.hex.setAttribute('aria-invalid', String(!hex));
      this.error.hidden = Boolean(hex);
      if (!hex) return false;
      this.picker.value = hex;
      this.sample.style.backgroundColor = hex;
      this.querySelector('[data-color-code]').textContent = hex;
      this.renderArtwork(hex);
      this.buttons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.swatch.toUpperCase() === hex)));
      try { sessionStorage.setItem(this.storageKey, hex); } catch (_) {}
      return true;
    }
    checkName(report) {
      const value = this.nameInput.value;
      const valid = value.trim().length > 0 && value.length <= (this.hasAttribute('data-vertical-crown') ? 12 : this.isMemorial ? 32 : 18) && /^[A-Za-zÀ-ÖØ-öø-ÿ0-9 '&.\-]+$/.test(value);
      this.nameInput.setCustomValidity(valid ? '' : this.dataset.nameInvalid);
      this.nameInput.setAttribute('aria-invalid', String(!valid));
      this.nameError.hidden = valid || (!report && !value);
      if (!valid && report) {
        this.nameInput.focus();
        this.nameInput.reportValidity();
      }
      return valid;
    }
    checkDates(report) {
      const birth = this.birthInput.value;
      const death = this.deathInput.value;
      const validDate = value => {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || value.startsWith('0000')) return false;
        const date = new Date(value + 'T12:00:00Z');
        return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
      };
      const birthValid = validDate(birth);
      const deathValid = validDate(death);
      const ordered = birthValid && deathValid && birth <= death;
      this.birthInput.setCustomValidity(birthValid ? '' : this.dataset.dateRequired);
      this.deathInput.setCustomValidity(!deathValid ? this.dataset.dateRequired : !ordered && birthValid ? this.dataset.dateOrder : '');
      this.birthInput.setAttribute('aria-invalid', String(!birthValid));
      this.deathInput.setAttribute('aria-invalid', String(!deathValid || (birthValid && !ordered)));
      this.dateError.textContent = birthValid && deathValid && !ordered ? this.dataset.dateOrder : this.dataset.dateRequired;
      this.dateError.hidden = ordered || (!report && (!birth || !death));
      if (!ordered && report) {
        const input = !birthValid ? this.birthInput : this.deathInput;
        input.focus();
        input.reportValidity();
      }
      return ordered;
    }
    formatMemorialDate(value, fallback) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return fallback;
      const [year, month, day] = value.split('-');
      return month + '.' + day + '.' + year;
    }
    stepFont(direction) {
      const count = this.fontInput.options.length;
      this.fontInput.selectedIndex = (this.fontInput.selectedIndex + direction + count) % count;
      this.fontInput.dispatchEvent(new Event('change', {bubbles: true}));
    }
    async loadNameFonts() {
      const family = this.fontInput.selectedOptions[0].dataset.fontFamily;
      const request = (this.fontRequest || 0) + 1;
      this.fontRequest = request;
      this.fontsReady = false;
      this.dataset.fontState = 'loading';
      this.canvas.hidden = true;
      this.previewStatus.textContent = this.dataset.fontLoading;
      this.previewStatus.hidden = false;
      try {
        const families = this.isMemorial ? [family, 'LpbName-pacifico', 'LpbName-classic'] : [family];
        const loaded = await Promise.all(families.map(font => document.fonts.load('100px "' + font + '"', this.nameInput.value.trim() || 'Your Name')));
        if (loaded.some(font => !font.length)) throw new Error('Font unavailable');
        if (request !== this.fontRequest || !this.isConnected) return;
        this.fontsReady = true;
        this.dataset.fontState = 'ready';
        this.canvas.width = this.hasAttribute('data-vertical-crown') ? 600 : 1200;
        this.canvas.height = this.hasAttribute('data-vertical-crown') ? 1400 : (this.isMemorial ? 780 : 480);
        this.canvas.hidden = false;
        this.previewStatus.hidden = true;
        this.renderArtwork(this.normalize(this.hex.value) || '#FFFFFF');
      } catch (_) {
        if (request !== this.fontRequest || !this.isConnected) return;
        this.dataset.fontState = 'error';
        this.previewStatus.textContent = this.dataset.fontError;
      }
    }
    renderName(hex) {
      if (!this.fontsReady) return;
      if (this.hasAttribute('data-vertical-crown')) { this.renderVerticalCrown(hex); return; }
      if (this.isMemorial) { this.renderMemorial(hex); return; }
      if (this.sportInputs?.length) { this.renderSport(hex); return; }
      const text = this.nameInput.value.trim() || this.dataset.namePlaceholder;
      const family = this.fontInput.selectedOptions[0].dataset.fontFamily;
      const context = this.canvas.getContext('2d');
      context.clearRect(0, 0, 1200, 480);
      context.font = '200px "' + family + '"';
      const measured = context.measureText(text);
      const inkWidth = Math.max(measured.width, measured.actualBoundingBoxLeft + measured.actualBoundingBoxRight);
      const inkHeight = measured.actualBoundingBoxAscent + measured.actualBoundingBoxDescent;
      const size = Math.min(1040 / Math.max(inkWidth, 1), 320 / Math.max(inkHeight, 1)) * 200;
      context.font = size + 'px "' + family + '"';
      const bounds = context.measureText(text);
      const left = bounds.actualBoundingBoxLeft;
      const right = bounds.actualBoundingBoxRight;
      const x = (1200 - left - right) / 2 + left;
      const y = (480 + bounds.actualBoundingBoxAscent - bounds.actualBoundingBoxDescent) / 2;
      context.fillStyle = hex;
      context.fillText(text, x, y);
      this.canvas.setAttribute('aria-label', text + ', ' + this.fontInput.value + ', ' + hex);
    }
    renderVerticalCrown(hex) {
      const chars = Array.from((this.nameInput.value.trim() || 'ALEX').replace(/\s+/g, ''));
      const family = this.fontInput.selectedOptions[0].dataset.fontFamily;
      const ctx = this.canvas.getContext('2d');
      ctx.clearRect(0, 0, 600, 1400);
      ctx.save();
      ctx.translate(300, 180);
      ctx.rotate(-12 * Math.PI / 180);
      ctx.fillStyle = hex;
      ctx.strokeStyle = hex;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      const shape = path => ctx.fill(new Path2D(path));
      shape('M -143 37 C -151 5 -167 -27 -177 -48 C -137 -24 -118 -18 -100 -50 C -87 -75 -79 -85 -74 -93 C -63 -62 -47 -34 -26 -49 C -11 -60 -6 -88 0 -106 C 6 -88 11 -60 26 -49 C 47 -34 63 -62 74 -93 C 79 -85 87 -75 100 -50 C 118 -18 137 -24 177 -48 C 167 -27 151 5 143 37 Q 0 17 -143 37 Z');
      const fleur = (x, y, scale, angle) => {
        ctx.save(); ctx.translate(x, y); ctx.rotate(angle); ctx.scale(scale, scale);
        shape('M 0 30 C -9 11 -7 0 -17 -3 C -38 -8 -43 -32 -28 -38 C -13 -44 -7 -22 -4 -12 C -10 -38 -19 -50 0 -76 C 19 -50 10 -38 4 -12 C 7 -22 13 -44 28 -38 C 43 -32 38 -8 17 -3 C 7 0 9 11 0 30 Z');
        ctx.fillRect(-19, 8, 38, 11);
        ctx.restore();
      };
      fleur(0, -102, 0.85, 0);
      fleur(-77, -85, 0.58, -0.24);
      fleur(77, -85, 0.58, 0.24);
      [[-177,-51],[177,-51]].forEach(([x,y]) => { ctx.beginPath(); ctx.arc(x,y,12,0,Math.PI*2); ctx.fill(); });
      shape('M -144 46 Q 0 23 144 46 L 138 94 Q 0 72 -138 94 Z');
      ctx.lineWidth = 9;
      ctx.beginPath(); ctx.moveTo(-143,105); ctx.quadraticCurveTo(0,84,143,105); ctx.stroke();
      ctx.save(); ctx.globalCompositeOperation = 'destination-out';
      [-94,-47,0,47,94].forEach(x => {
        const y = 59 + 0.0008*x*x;
        shape('M '+x+' '+(y-13)+' L '+(x+11)+' '+y+' L '+x+' '+(y+13)+' L '+(x-11)+' '+y+' Z');
      });
      [-105,-52,52,105].forEach(x => {
        ctx.beginPath(); ctx.ellipse(x, 5, 9, 13, x < 0 ? -0.3 : 0.3, 0, Math.PI*2); ctx.fill();
      });
      ctx.restore(); ctx.restore();
      const step = 1000 / Math.max(chars.length, 1);
      let size = Math.min(190, step * .78);
      ctx.font = size + 'px "' + family + '"';
      const maxWidth = Math.max(...chars.map(ch => { const b = ctx.measureText(ch); return Math.max(b.width, b.actualBoundingBoxLeft + b.actualBoundingBoxRight); }), 1);
      if (maxWidth > 400) size *= 400 / maxWidth;
      ctx.font = size + 'px "' + family + '"';
      ctx.fillStyle = hex; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      chars.forEach((ch, i) => ctx.fillText(ch, 300, 360 + step * (i + .5)));
      this.canvas.setAttribute('aria-label', 'Royal fleur-de-lis crown above vertical name ' + chars.join('') + ', ' + this.fontInput.value + ', ' + hex);
    }
    async getSportMask(url) {
      if (!this.sportArtCache.has(url)) this.sportArtCache.set(url, this.makeMemorialInkMask(url));
      return this.sportArtCache.get(url);
    }
    async loadSportArt() {
      const choice = this.sportInputs.find(input => input.checked);
      if (!choice) return;
      const request = (this.sportRequest || 0) + 1;
      this.sportRequest = request;
      this.sportReady = false;
      this.canvas.hidden = true;
      this.previewStatus.hidden = false;
      this.previewStatus.textContent = 'Loading sports artwork…';
      try {
        const artwork = await this.getSportMask(choice.dataset.sportArt);
        if (request !== this.sportRequest || !this.isConnected) return;
        this.sportMask = artwork;
        this.sportReady = true;
        if (this.fontsReady) {
          this.canvas.hidden = false;
          this.previewStatus.hidden = true;
          this.renderArtwork(this.normalize(this.hex.value) || '#FFFFFF');
        }
      } catch (_) {
        if (request !== this.sportRequest || !this.isConnected) return;
        this.previewStatus.textContent = 'Sports artwork could not load. Please refresh the page.';
      }
    }
    renderSport(hex) {
      if (!this.fontsReady || !this.sportReady) { this.canvas.hidden = true; this.previewStatus.hidden = false; return; }
      this.canvas.hidden = false; this.previewStatus.hidden = true;
      const c=this.canvas.getContext('2d'), name=this.nameInput.value.trim() || this.dataset.namePlaceholder;
      const sport=this.sportInputs.find(input => input.checked)?.value || 'Soccer', family=this.fontInput.selectedOptions[0].dataset.fontFamily;
      c.clearRect(0,0,1200,480); c.strokeStyle=hex; c.fillStyle=hex; c.lineWidth=12; c.lineCap='round'; c.lineJoin='round';
      const art=this.sportMask, scale=Math.min(310 / art.width, 310 / art.height);
      const w=art.width*scale, h=art.height*scale;
      const layer=document.createElement('canvas'); layer.width=1200; layer.height=480;
      const layerCtx=layer.getContext('2d');
      layerCtx.drawImage(art,190-w/2,235-h/2,w,h);
      layerCtx.globalCompositeOperation='source-in'; layerCtx.fillStyle=hex; layerCtx.fillRect(0,0,1200,480);
      c.drawImage(layer,0,0);
      c.font='180px "'+family+'"'; const m=c.measureText(name); const size=Math.min(180,760/Math.max(m.width,1)*180);
      c.font=size+'px "'+family+'"';c.textAlign='center';c.textBaseline='middle';c.fillText(name,790,215);
      c.fillRect(410,340,720,12);c.fillRect(435,370,670,12);
      this.canvas.setAttribute('aria-label',sport+', '+name+', '+this.fontInput.value+', '+hex);
    }
    drawSportBall(c, sport, color) {
      c.strokeStyle=color; c.fillStyle=color; c.lineWidth=12; c.lineCap='round'; c.lineJoin='round';
      if(sport==='Football') {
        c.beginPath(); c.moveTo(-150,0); c.bezierCurveTo(-100,-110,100,-110,150,0); c.bezierCurveTo(100,110,-100,110,-150,0); c.closePath(); c.stroke();
        c.beginPath(); c.moveTo(-55,0); c.lineTo(55,0); [-35,0,35].forEach(x=>{c.moveTo(x,-20);c.lineTo(x,20)}); c.stroke();
      } else {
        c.beginPath(); c.arc(0,0,145,0,Math.PI*2); c.stroke();
        if(sport==='Basketball') {
          c.beginPath(); c.moveTo(-145,0);c.lineTo(145,0);c.moveTo(0,-145);c.lineTo(0,145);c.moveTo(-110,-95);c.quadraticCurveTo(80,0,-110,95);c.moveTo(110,-95);c.quadraticCurveTo(-80,0,110,95);c.stroke();
        } else if(sport==='Baseball'||sport==='Softball') {
          c.beginPath();c.moveTo(-110,-90);c.bezierCurveTo(-35,-50,-35,50,-110,90);c.moveTo(110,-90);c.bezierCurveTo(35,-50,35,50,110,90);c.stroke();
          [-50,-20,10,40].forEach(y=>{c.beginPath();c.moveTo(-90,y-9);c.lineTo(-55,y+9);c.moveTo(55,y-9);c.lineTo(90,y+9);c.stroke()});
        } else if(sport==='Volleyball') {
          c.beginPath();c.moveTo(0,-145);c.bezierCurveTo(-25,-75,-100,-40,-130,-35);c.moveTo(0,-145);c.bezierCurveTo(70,-90,80,-10,60,45);c.moveTo(60,45);c.bezierCurveTo(5,45,-65,65,-90,112);c.moveTo(-130,-35);c.bezierCurveTo(-70,0,-65,65,-90,112);c.moveTo(60,45);c.bezierCurveTo(90,80,100,95,110,90);c.stroke();
        } else {
          c.beginPath();for(let i=0;i<5;i++){let a=-Math.PI/2+i*2*Math.PI/5,x=48*Math.cos(a),y=48*Math.sin(a);if(i===0)c.moveTo(x,y);else c.lineTo(x,y)}c.closePath();c.fill();
          c.beginPath();for(let i=0;i<5;i++){let a=-Math.PI/2+i*2*Math.PI/5;c.moveTo(48*Math.cos(a),48*Math.sin(a));c.lineTo(138*Math.cos(a),138*Math.sin(a));}c.stroke();
        }
      }
    }
    async makeMemorialInkMask(url) {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.src = url;
      await image.decode();
      const mask = document.createElement('canvas');
      mask.width = image.naturalWidth;
      mask.height = image.naturalHeight;
      const context = mask.getContext('2d', {willReadFrequently: true});
      context.drawImage(image, 0, 0);
      const pixels = context.getImageData(0, 0, mask.width, mask.height);
      let minX = mask.width, minY = mask.height, maxX = -1, maxY = -1;
      for (let i = 0; i < pixels.data.length; i += 4) {
        const light = Math.max(pixels.data[i], pixels.data[i + 1], pixels.data[i + 2]);
        const opacity = (255 - light) * pixels.data[i + 3] / 255;
        pixels.data[i] = pixels.data[i + 1] = pixels.data[i + 2] = 255;
        pixels.data[i + 3] = opacity >= 128 ? 255 : 0;
        if (pixels.data[i + 3]) {
          const index = i / 4, x = index % mask.width, y = Math.floor(index / mask.width);
          minX = Math.min(minX, x); maxX = Math.max(maxX, x);
          minY = Math.min(minY, y); maxY = Math.max(maxY, y);
        }
      }
      if (maxX < minX || maxY < minY) throw new Error('Empty artwork');
      context.putImageData(pixels, 0, 0);
      const cropped = document.createElement('canvas');
      cropped.width = maxX - minX + 1;
      cropped.height = maxY - minY + 1;
      cropped.getContext('2d').drawImage(mask, minX, minY, cropped.width, cropped.height, 0, 0, cropped.width, cropped.height);
      return cropped;
    }
    loadMemorialThumbnails() {
      const draw = async canvas => {
        try {
          const artwork = await this.makeMemorialInkMask(canvas.dataset.thumbSource);
          const scale = Math.min((canvas.width - 16) / artwork.width, (canvas.height - 16) / artwork.height);
          const width = artwork.width * scale, height = artwork.height * scale;
          const context = canvas.getContext('2d');
          context.clearRect(0, 0, canvas.width, canvas.height);
          context.drawImage(artwork, (canvas.width - width) / 2, (canvas.height - height) / 2, width, height);
          canvas.dataset.thumbState = 'ready';
        } catch (_) { canvas.dataset.thumbState = 'error'; }
      };
      const thumbnails = [...this.querySelectorAll('[data-memorial-thumb]')];
      if (!('IntersectionObserver' in window)) { thumbnails.forEach(draw); return; }
      this.thumbnailObserver = new IntersectionObserver(entries => entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        this.thumbnailObserver.unobserve(entry.target);
        draw(entry.target);
      }), {rootMargin: '200px'});
      thumbnails.forEach(canvas => this.thumbnailObserver.observe(canvas));
    }
    async loadMemorialTheme() {
      const choice = this.memorialThemeRadios.find(radio => radio.checked);
      if (!choice) return;
      const request = (this.themeRequest || 0) + 1;
      this.themeRequest = request;
      this.selectedMemorialTheme = choice;
      this.querySelector('[data-selected-theme]').textContent = choice.value;
      this.themeReady = false;
      this.themeLoadFailed = false;
      this.dataset.themeState = 'loading';
      this.canvas.hidden = true;
      this.previewStatus.hidden = false;
      this.previewStatus.textContent = this.dataset.themeLoading;
      try {
        let artwork = this.memorialArtCache.get(choice.dataset.themeArt);
        if (!artwork) {
          artwork = this.hasAttribute('data-detailed-memorial') ? await this.makeMemorialInkMask(choice.dataset.themeArt) : new Image();
          if (!this.hasAttribute('data-detailed-memorial')) {
            artwork.crossOrigin = 'anonymous';
            artwork.src = choice.dataset.themeArt;
            await artwork.decode();
          }
          this.memorialArtCache.set(choice.dataset.themeArt, artwork);
        }
        if (request !== this.themeRequest || !this.isConnected) return;
        this.selectedMemorialArt = artwork;
        this.themeReady = true;
        this.dataset.themeState = 'ready';
        this.canvas.hidden = !this.fontsReady;
        this.previewStatus.hidden = Boolean(this.fontsReady);
        this.renderArtwork(this.normalize(this.hex.value) || '#FFFFFF');
      } catch (_) {
        if (request !== this.themeRequest || !this.isConnected) return;
        this.themeLoadFailed = true;
        this.dataset.themeState = 'error';
        this.previewStatus.textContent = this.dataset.themeError;
      }
    }
    renderMemorial(hex) {
      if (this.memorialThemeRadios?.length) {
        if (!this.themeReady) {
          this.canvas.hidden = true;
          this.previewStatus.hidden = false;
          this.previewStatus.textContent = this.themeLoadFailed ? this.dataset.themeError : this.dataset.themeLoading;
          return;
        }
        this.renderMemorialCollection(hex);
        return;
      }
      const context = this.canvas.getContext('2d');
      context.clearRect(0, 0, 1200, 780);
      context.fillStyle = hex;
      const fitText = (text, family, maximumSize, centerY, maximumWidth, maximumHeight) => {
        context.font = maximumSize + 'px "' + family + '"';
        let bounds = context.measureText(text);
        const width = Math.max(bounds.width, bounds.actualBoundingBoxLeft + bounds.actualBoundingBoxRight, 1);
        const height = Math.max(bounds.actualBoundingBoxAscent + bounds.actualBoundingBoxDescent, 1);
        const scale = Math.min(1, maximumWidth / width, maximumHeight / height);
        context.font = maximumSize * scale + 'px "' + family + '"';
        bounds = context.measureText(text);
        const x = (1200 - bounds.actualBoundingBoxLeft - bounds.actualBoundingBoxRight) / 2 + bounds.actualBoundingBoxLeft;
        const y = centerY + (bounds.actualBoundingBoxAscent - bounds.actualBoundingBoxDescent) / 2;
        context.fillText(text, x, y);
      };
      const name = this.nameInput.value.trim() || this.dataset.namePlaceholder;
      const family = this.fontInput.selectedOptions[0].dataset.fontFamily;
      const birth = this.formatMemorialDate(this.birthInput.value, '01.15.1950');
      const death = this.formatMemorialDate(this.deathInput.value, '09.20.2025');
      fitText(this.dataset.memorialHeading, 'LpbName-pacifico', 100, 150, 1000, 110);
      fitText(name, family, 165, 315, 1060, 145);
      fitText(birth + ' — ' + death, 'LpbName-classic', 55, 450, 1000, 65);
      context.save();
      context.translate(600, 520);
      context.beginPath();
      context.moveTo(0, 20);
      context.bezierCurveTo(-38, -12, -64, 40, 0, 88);
      context.bezierCurveTo(64, 40, 38, -12, 0, 20);
      context.fill();
      for (const side of [-1, 1]) {
        context.save();
        context.scale(side, 1);
        context.strokeStyle = hex;
        context.lineWidth = 12;
        context.lineCap = 'round';
        context.beginPath();
        context.moveTo(65, 65);
        context.quadraticCurveTo(240, 118, 400, 12);
        context.stroke();
        [[100,64,-0.5],[158,74,-0.3],[218,72,-0.1],[278,58,0.15],[335,34,0.35]].forEach(([x,y,angle]) => {
          context.beginPath();
          context.ellipse(x, y - 18, 29, 12, angle, 0, Math.PI * 2);
          context.fill();
        });
        context.restore();
      }
      context.restore();
      fitText(this.dataset.memorialFooter, 'LpbName-pacifico', 65, 700, 980, 80);
      this.canvas.setAttribute('aria-label', [this.dataset.memorialHeading, name, birth, death, this.fontInput.value, hex].join(', '));
    }
    renderMemorialCollection(hex) {
      const context = this.canvas.getContext('2d');
      context.clearRect(0, 0, 1200, 780);
      context.globalCompositeOperation = 'source-over';
      context.fillStyle = hex;
      const fit = (text, family, size, centerY, maximumWidth, maximumHeight) => {
        context.font = size + 'px "' + family + '"';
        let bounds = context.measureText(text);
        const width = Math.max(bounds.width, bounds.actualBoundingBoxLeft + bounds.actualBoundingBoxRight, 1);
        const height = Math.max(bounds.actualBoundingBoxAscent + bounds.actualBoundingBoxDescent, 1);
        context.font = size * Math.min(1, maximumWidth / width, maximumHeight / height) + 'px "' + family + '"';
        bounds = context.measureText(text);
        context.fillText(text, (1200 - bounds.actualBoundingBoxLeft - bounds.actualBoundingBoxRight) / 2 + bounds.actualBoundingBoxLeft, centerY + (bounds.actualBoundingBoxAscent - bounds.actualBoundingBoxDescent) / 2);
      };
      const name = this.nameInput.value.trim() || this.dataset.namePlaceholder;
      const birth = this.formatMemorialDate(this.birthInput.value, '01.15.1950');
      const death = this.formatMemorialDate(this.deathInput.value, '09.20.2025');
      fit(this.dataset.memorialHeading, 'LpbName-pacifico', 91, 83, 1000, 100);
      fit(name, this.fontInput.selectedOptions[0].dataset.fontFamily, 150, 198, 1060, 120);
      fit(birth + ' — ' + death, 'LpbName-classic', 52, 302, 1000, 56);
      const height = Number(this.selectedMemorialTheme.dataset.artHeight);
      const scale = Math.min(1000 / this.selectedMemorialArt.width, height / this.selectedMemorialArt.height);
      const artworkWidth = this.selectedMemorialArt.width * scale;
      const artworkHeight = this.selectedMemorialArt.height * scale;
      context.drawImage(this.selectedMemorialArt, (1200 - artworkWidth) / 2, Number(this.selectedMemorialTheme.dataset.artY) + (height - artworkHeight) / 2, artworkWidth, artworkHeight);
      context.globalCompositeOperation = 'source-in';
      context.fillRect(0, 0, 1200, 780);
      context.globalCompositeOperation = 'source-over';
      fit(this.dataset.memorialFooter, 'LpbName-pacifico', 60, 731, 980, 64);
      this.canvas.hidden = false;
      this.previewStatus.hidden = true;
      this.canvas.setAttribute('aria-label', [this.selectedMemorialTheme.value, this.dataset.memorialHeading, name, birth, death, this.fontInput.value, hex].join(', '));
    }
    async loadArtwork() {
      if (!this.canvas || !this.dataset.artwork) return;
      try {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.src = this.dataset.artwork;
        await image.decode();
        const mask = document.createElement('canvas');
        mask.width = image.naturalWidth;
        mask.height = image.naturalHeight;
        const context = mask.getContext('2d', {willReadFrequently: true});
        context.drawImage(image, 0, 0);
        const pixels = context.getImageData(0, 0, mask.width, mask.height);
        for (let i = 0; i < pixels.data.length; i += 4) {
          const brightness = (pixels.data[i] + pixels.data[i + 1] + pixels.data[i + 2]) / 3;
          const opacity = (255 - brightness) * pixels.data[i + 3] / 255;
          pixels.data[i] = pixels.data[i + 1] = pixels.data[i + 2] = 255;
          pixels.data[i + 3] = opacity < 24 ? 0 : opacity;
        }
        context.putImageData(pixels, 0, 0);
        this.artworkMask = mask;
        this.canvas.width = mask.width;
        this.canvas.height = mask.height;
        this.canvas.hidden = false;
        this.previewStatus.hidden = true;
        this.renderArtwork(this.normalize(this.hex.value) || '#FFFFFF');
      } catch (_) {
        this.previewStatus.textContent = this.dataset.previewError;
        this.previewStatus.hidden = false;
      }
    }
    renderArtwork(hex) {
      if (this.hasAttribute('data-name-decal')) {
        this.renderName(hex);
        this.setPreviewBackground(hex);
        return;
      }
      if (!this.artworkMask) return;
      const context = this.canvas.getContext('2d');
      context.clearRect(0, 0, this.canvas.width, this.canvas.height);
      context.globalCompositeOperation = 'source-over';
      context.drawImage(this.artworkMask, 0, 0);
      context.globalCompositeOperation = 'source-in';
      context.fillStyle = hex;
      context.fillRect(0, 0, this.canvas.width, this.canvas.height);
      context.globalCompositeOperation = 'source-over';
      this.setPreviewBackground(hex);
      this.canvas.setAttribute('aria-label', this.dataset.previewLabel + ' ' + hex);
    }
    setPreviewBackground(hex) {
      const rgb = hex.slice(1).match(/../g).map(c => parseInt(c, 16));
      const brightness = rgb[0] * 0.299 + rgb[1] * 0.587 + rgb[2] * 0.114;
      this.querySelector('[data-preview-surface]').dataset.light = String(brightness < 105);
    }
    validate() {
      if (this.nameInput) {
        if (!this.checkName(true)) return false;
        if (this.isMemorial && !this.checkDates(true)) return false;
        if (this.memorialThemeRadios?.length && !this.themeReady) {
          this.previewStatus.hidden = false;
          return false;
        }
        if (this.sportInputs?.length && !this.sportReady) { this.previewStatus.hidden = false; return false; }
        if (!this.fontsReady) {
          this.previewStatus.hidden = false;
          return false;
        }
        this.nameInput.value = this.nameInput.value.trim();
      }
      if (this.update(this.hex.value)) {
        this.hex.value = this.normalize(this.hex.value);
        return true;
      }
      this.hex.focus();
      this.hex.reportValidity();
      return false;
    }
  }
  customElements.define('lpb-decal-color', LpbDecalColor);
})();





;(() => {
  const elementName = 'lpb-decal-color';
  const start = () => {
    const C = customElements.get(elementName);
    if (!C) return;
    if (!C.prototype.lpbGalleryWrapped) {
      const original = C.prototype.renderArtwork;
      C.prototype.renderArtwork = function(hex) {
        const result = original.call(this, hex);
        requestAnimationFrame(() => this.lpbSyncGallery && this.lpbSyncGallery());
        return result;
      };
      C.prototype.lpbGalleryWrapped = true;
    }
    document.querySelectorAll(elementName).forEach(control => {
      if (control.dataset.galleryLinked) return;
      const section = control.closest('.shopify-section') || document;
      const gallery = section.querySelector('media-gallery:not([id*="duplicate"])');
      const source = control.querySelector('[data-decal-preview]');
      const sourceSurface = control.querySelector('[data-preview-surface]');
      const sourceStatus = control.querySelector('[data-preview-status]');
      if (!gallery || !source || !sourceSurface) return;
      control.dataset.galleryLinked = 'true';
      const inlinePreview = control.querySelector('.lpb-decal-preview');
      if (inlinePreview) {
        control.insertBefore(inlinePreview, control.firstChild);
        const tools = document.createElement('div');
        tools.className = 'lpb-mobile-preview-tools';
        const title = document.createElement('span');
        title.textContent = 'Live preview';
        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.textContent = 'Hide';
        toggle.setAttribute('aria-expanded','true');
        const enlarge = document.createElement('button');
        enlarge.type = 'button';
        enlarge.textContent = 'Enlarge';
        enlarge.setAttribute('aria-pressed','false');
        toggle.addEventListener('click', () => {
          const collapsed = inlinePreview.classList.toggle('lpb-preview-collapsed');
          toggle.textContent = collapsed ? 'Show' : 'Hide';
          toggle.setAttribute('aria-expanded', String(!collapsed));
          enlarge.hidden = collapsed;
          if (collapsed) { inlinePreview.classList.remove('lpb-preview-enlarged'); enlarge.textContent = 'Enlarge'; enlarge.setAttribute('aria-pressed','false'); }
        });
        enlarge.addEventListener('click', () => {
          const expanded = inlinePreview.classList.toggle('lpb-preview-enlarged');
          enlarge.textContent = expanded ? 'Smaller' : 'Enlarge';
          enlarge.setAttribute('aria-pressed', String(expanded));
        });
        tools.append(title, enlarge, toggle);
        inlinePreview.insertBefore(tools, inlinePreview.firstChild);
      }
      const photos = [...gallery.querySelectorAll(':scope > slider-component')];
      if (!photos.length) return;
      const bar = document.createElement('div');
      bar.className = 'lpb-gallery-preview-switch';
      const photoButton = document.createElement('button');
      photoButton.type = 'button'; photoButton.textContent = 'Product photos';
      const previewButton = document.createElement('button');
      previewButton.type = 'button'; previewButton.textContent = 'Your design preview';
      photoButton.setAttribute('aria-pressed','true');
      previewButton.setAttribute('aria-pressed','false');
      bar.append(photoButton, previewButton);
      const panel = document.createElement('div');
      panel.className = 'lpb-gallery-preview-panel';
      if (control.hasAttribute('data-vertical-crown')) panel.setAttribute('data-vertical-crown', '');
      panel.hidden = true;
      const heading = document.createElement('h2');
      heading.textContent = 'Your design preview';
      const surface = document.createElement('div');
      surface.className = 'lpb-gallery-preview-surface';
      const canvas = document.createElement('canvas');
      canvas.setAttribute('role','img');
      const status = document.createElement('p');
      status.setAttribute('role','status');
      surface.append(canvas, status); panel.append(heading, surface);
      gallery.insertBefore(bar, gallery.firstChild);
      gallery.insertBefore(panel, photos[0]);
      const updateFloatingPreview = () => {
        const g = gallery.getBoundingClientRect(), c = control.getBoundingClientRect();
        const active = !panel.hidden && matchMedia('(min-width: 750px)').matches &&
          (control.hasAttribute('data-vertical-crown') ? g.top < 250 && c.bottom > 260 : g.bottom < 180 && c.bottom > 80) && c.top < innerHeight - 50;
        panel.classList.toggle('lpb-gallery-floating', active);
      };
      let scrollQueued = false;
      const onScroll = () => {
        if (scrollQueued) return;
        scrollQueued = true;
        requestAnimationFrame(() => { scrollQueued = false; updateFloatingPreview(); });
      };
      window.addEventListener('scroll', onScroll, {passive:true});
      window.addEventListener('resize', onScroll);

      const setView = show => {
        panel.hidden = !show;
        photos.forEach(el => { el.style.display = show ? 'none' : ''; });
        photoButton.setAttribute('aria-pressed', String(!show));
        previewButton.setAttribute('aria-pressed', String(show));
        updateFloatingPreview();
      };
      photoButton.addEventListener('click', () => setView(false));
      previewButton.addEventListener('click', () => {control.lpbSyncGallery(); setView(true);});
      control.lpbSyncGallery = () => {
        if (!control.isConnected || !canvas.isConnected) return;
        surface.style.backgroundColor = getComputedStyle(sourceSurface).backgroundColor;
        canvas.hidden = source.hidden;
        status.hidden = !source.hidden;
        status.textContent = sourceStatus?.textContent || 'Loading preview…';
        if (!source.hidden && source.width && source.height) {
          if (canvas.width !== source.width) canvas.width = source.width;
          if (canvas.height !== source.height) canvas.height = source.height;
          const ctx = canvas.getContext('2d'); ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(source, 0, 0);
          canvas.setAttribute('aria-label', source.getAttribute('aria-label') || 'Your decal preview');
        }
      };
      const observer = new MutationObserver(() => control.lpbSyncGallery());
      observer.observe(source, {attributes:true, attributeFilter:['hidden','aria-label','width','height']});
      if(sourceStatus) observer.observe(sourceStatus,{attributes:true,childList:true,characterData:true,subtree:true});
      control.addEventListener('input', () => { if (matchMedia('(min-width: 750px)').matches) setView(true); });
      control.addEventListener('change', () => { if (matchMedia('(min-width: 750px)').matches) setView(true); });
      control.addEventListener('click', event => {
        if (event.target.closest('[data-swatch],[data-font-previous],[data-font-next]') && matchMedia('(min-width: 750px)').matches) setView(true);
      });
      control.lpbSyncGallery();
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',start);
  else start();
  document.addEventListener('shopify:section:load', start);
})();
